import { CategoriesStore } from "@/stores/categories-store.core";
import { useEffect } from "react";
import { useI18nContext } from "@procore/core-react";
import { useToastAlertContext } from "@procore/toast-alert";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ProjectCategory } from "./types";
import { CATEGORIES_TRANSLATIONS, type CategoryMoveUpdate } from "./utils";

type UseCategoriesOptions = {
   onSuccess?: () => void;
};

export function useCategories(projectId: string, options?: UseCategoriesOptions) {
   const queryClient = useQueryClient();
   const { showToast } = useToastAlertContext();
   const I18n = useI18nContext();

   const { data, isLoading, isError } = useQuery({
      queryKey: ["categories", projectId],
      queryFn: () => CategoriesStore.getProjectCategories(projectId).payload,
      enabled: !!projectId,
      retry: 3,
      refetchOnWindowFocus: (query) => query.state.status !== "error",
   });

   useEffect(() => {
      if (isError) {
         showToast.error(I18n.t(`${CATEGORIES_TRANSLATIONS}.fetch_error`));
      }
   }, [isError, showToast, I18n]);

   const invalidate = () => queryClient.invalidateQueries({ queryKey: ["categories", projectId] });

   const handleSuccess = () => {
      invalidate();
      options?.onSuccess?.();
   };

   const createMutation = useMutation({
      mutationFn: ({ name, parent_id }: { name: string; parent_id: string | null }) =>
         CategoriesStore.createProjectCategory(projectId, { name, parent_id }).payload,
      onSuccess: handleSuccess,
      onError: () => showToast.error(I18n.t(`${CATEGORIES_TRANSLATIONS}.create_error`)),
      retry: false,
   });

   const renameMutation = useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) =>
         CategoriesStore.updateProjectCategory(projectId, id, { name }).payload,
      onSuccess: handleSuccess,
      onError: () => showToast.error(I18n.t(`${CATEGORIES_TRANSLATIONS}.rename_error`)),
      retry: 3,
   });

   const deleteMutation = useMutation({
      mutationFn: ({ id }: { id: string }) =>
         CategoriesStore.deleteProjectCategory(projectId, id).payload,
      onSuccess: handleSuccess,
      onError: () => showToast.error(I18n.t(`${CATEGORIES_TRANSLATIONS}.delete_error`)),
      retry: 3,
   });

   const moveMutation = useMutation({
      mutationFn: ({ id, parent_id, sequence }: CategoryMoveUpdate) =>
         CategoriesStore.updateProjectCategory(projectId, id, {
            parent_id,
            sequence,
         }).payload,
      onSuccess: handleSuccess,
      onError: () => showToast.error(I18n.t(`${CATEGORIES_TRANSLATIONS}.move_error`)),
      retry: 3,
   });

   return {
      categories: (data?.data ?? []) as ProjectCategory[],
      create: createMutation.mutate,
      rename: renameMutation.mutate,
      remove: deleteMutation.mutate,
      move: moveMutation.mutate,
      isLoading,
      isCreating: createMutation.isPending,
      isRenaming: renameMutation.isPending,
      isDeleting: deleteMutation.isPending,
      isMoving: moveMutation.isPending,
   };
}
